import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import axiosWrapper from '../../utils/apiRequests/axiosWrapper';
import extractAuthFromApiBody from '../../utils/extractAuthFromApiBody';
import { updateUserInfo } from '../../features/user/userSlice';
import StepperModal from '../StepperModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import Dropdown from '../Dropdown';
import { THEME_METADATA } from '../../utils/themeConstants';

const STEPS = ['Email', 'Name', 'Preferences', 'Confirmation'];

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: '☀️' },
  { value: 'dark', label: 'Dark', icon: '🌙' },
];

const RegisterModal = ({ onClose, switchToLogin }) => {
  const dispatch = useDispatch();
  const { colors } = useThemeStyles();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dailyWordGoal, setDailyWordGoal] = useState(500);
  const [theme, setTheme] = useState('light');

  // Validation state
  const [emailError, setEmailError] = useState('');

  const resetState = () => {
    setActiveStep(0);
    setEmail('');
    setFullName('');
    setPassword('');
    setConfirmPassword('');
    setDailyWordGoal(500);
    setTheme('light');
    setError('');
    setEmailError('');
  };

  const handleClose = () => {
    if (!loading) {
      resetState();
      onClose();
    }
  };

  const handleNext = async () => {
    // Step 1: Validate email and check if it's in use
    if (activeStep === 0) {
      setEmailError('');
      setError('');

      // Basic email validation
      const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]+$/;
      if (!email.trim()) {
        setEmailError('Email is required');
        return;
      }
      if (!emailRegex.test(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }

      // Note: Email availability check happens on final submit since there's no separate endpoint
      // Format validation passes, proceed to next step
      setActiveStep((prev) => prev + 1);
      return;
    }

    // Other steps just advance
    setActiveStep((prev) => prev + 1);
    setError('');
  };

  const handleBack = () => {
    if (!loading) {
      setActiveStep((prev) => prev - 1);
      setError('');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    // Final validation
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    const registerData = {
      email,
      fullName,
      password,
      confirmPassword,
      theme,
      dailyWordGoal,
    };

    try {
      const data = await axiosWrapper({
        method: 'post',
        path: 'register',
        data: registerData,
        optimistic: false,
      });

      const { user: registeredUser, token } = extractAuthFromApiBody(data?.data);

      if (registeredUser?._id) {
        if (!token) {
          setError(
            'Account created but no session token was returned. Try logging in, or check your API response in production.'
          );
          setLoading(false);
          return;
        }
        localStorage.setItem('authToken', token);
        dispatch(updateUserInfo(registeredUser));
        handleClose();
      } else {
        const respErrors = data?.error?.response?.data?.errors || [];
        const respError = data?.error?.response?.data?.error;

        const errorMessages = [];
        if (respError) errorMessages.push(respError);

        respErrors.forEach((err) => {
          errorMessages.push(err.msg || err);
        });

        setError(errorMessages.join('. ') || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0: // Email
        return email.trim() !== '' && !emailError;
      case 1: // Name
        return fullName.trim() !== '';
      case 2: // Preferences
        return true; // Daily word goal and theme have defaults
      case 3: // Confirmation
        return password.trim() !== '' && confirmPassword.trim() !== '';
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    const currentStepLabel = STEPS[activeStep];

    switch (currentStepLabel) {
      case 'Email':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors?.text?.secondary || 'text.secondary',
              }}
            >
              Enter your email address to get started. We&apos;ll verify it&apos;s available.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              error={!!emailError}
              helperText={emailError}
              fullWidth
              disabled={loading}
              sx={{
                '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                input: { color: colors?.text?.primary || 'text.primary' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
                  {
                    WebkitBoxShadow: `0 0 0 100px ${
                      colors?.background?.surface || colors?.paper?.background || '#111827'
                    } inset`,
                    WebkitTextFillColor: colors?.text?.primary || 'inherit',
                  },
              }}
            />
          </Box>
        );

      case 'Name':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors?.text?.secondary || 'text.secondary',
              }}
            >
              What should we call you?
            </Typography>
            <TextField
              label="Full Name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              disabled={loading}
              sx={{
                '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                input: { color: colors?.text?.primary || 'text.primary' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
                  {
                    WebkitBoxShadow: `0 0 0 100px ${
                      colors?.background?.surface || colors?.paper?.background || '#111827'
                    } inset`,
                    WebkitTextFillColor: colors?.text?.primary || 'inherit',
                  },
              }}
            />
          </Box>
        );

      case 'Preferences':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors?.text?.secondary || 'text.secondary',
              }}
            >
              Set up your writing preferences. You can change these later in settings.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Dropdown
                label="Theme"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                options={THEME_OPTIONS}
                disabled={loading}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: colors?.text?.secondary || 'text.secondary',
                }}
              >
                Theme applies to the entire PlotWyze app and syncs across your devices.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Daily word count goal"
                type="number"
                value={dailyWordGoal}
                onChange={(e) => setDailyWordGoal(Math.max(0, parseInt(e.target.value, 10) || 0))}
                fullWidth
                inputProps={{ min: 0 }}
                disabled={loading}
                sx={{
                  '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                  input: { color: colors?.text?.primary || 'text.primary' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
                    {
                      WebkitBoxShadow: `0 0 0 100px ${
                        colors?.background?.surface || colors?.paper?.background || '#111827'
                      } inset`,
                      WebkitTextFillColor: colors?.text?.primary || 'inherit',
                    },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: colors?.text?.secondary || 'text.secondary',
                }}
              >
                Set your daily writing goal. You can track your progress and maintain streaks.
              </Typography>
            </Box>
          </Box>
        );

      case 'Confirmation':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: colors?.text?.secondary || 'text.secondary',
              }}
            >
              Review your information and create a password to complete registration.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                fontStyle: 'italic',
                color: colors?.text?.secondary || 'text.secondary',
              }}
            >
              Note: We&apos;ll verify your email is available when you submit. If it&apos;s already
              in use, you&apos;ll see an error and can go back to change it.
            </Typography>

            {/* Review Section */}
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: `1px solid ${colors?.border?.subtle || 'divider'}`,
                backgroundColor: colors?.background?.secondary || 'background.secondary',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Account Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors?.text?.secondary || 'text.secondary' }}
                  >
                    Email:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors?.text?.secondary || 'text.secondary' }}
                  >
                    Name:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {fullName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors?.text?.secondary || 'text.secondary' }}
                  >
                    Theme:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {THEME_METADATA[theme]?.name || theme}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors?.text?.secondary || 'text.secondary' }}
                  >
                    Daily Goal:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {dailyWordGoal.toLocaleString()} words
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Password Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                disabled={loading}
                helperText="Must be at least 8 characters"
                sx={{
                  '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                  input: { color: colors?.text?.primary || 'text.primary' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
                    {
                      WebkitBoxShadow: `0 0 0 100px ${
                        colors?.background?.surface || colors?.paper?.background || '#111827'
                      } inset`,
                      WebkitTextFillColor: colors?.text?.primary || 'inherit',
                    },
                }}
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                disabled={loading}
                sx={{
                  '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                  input: { color: colors?.text?.primary || 'text.primary' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                    '&:hover fieldset': { borderColor: 'primary.main' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus':
                    {
                      WebkitBoxShadow: `0 0 0 100px ${
                        colors?.background?.surface || colors?.paper?.background || '#111827'
                      } inset`,
                      WebkitTextFillColor: colors?.text?.primary || 'inherit',
                    },
                }}
              />
            </Box>

            <Typography
              variant="body2"
              align="center"
              sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors?.border?.subtle || 'divider'}` }}
            >
              Already have an account?{' '}
              <Typography
                component="span"
                onClick={switchToLogin}
                sx={{
                  color: colors?.interactive?.primary || 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: colors?.interactive?.hover || 'primary.dark',
                  },
                }}
              >
                Log in
              </Typography>
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StepperModal
      open
      onClose={handleClose}
      title="Create Account"
      steps={STEPS}
      activeStep={activeStep}
      onNext={handleNext}
      onBack={handleBack}
      onSubmit={handleSubmit}
      canProceed={canProceed}
      loading={loading}
      error={error}
      submitButtonText="Register"
      width={520}
      renderStepContent={renderStepContent}
      ariaLabelledby="register-modal-title"
      ariaDescribedby="register-modal-description"
    />
  );
};

RegisterModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  switchToLogin: PropTypes.func.isRequired,
};

export default RegisterModal;
